import type { jsPDF } from 'jspdf';

/**
 * The pieces every PDF this app exports needs: the stylesheet's colours, an A4 page, a cursor that
 * flows blocks down it, and the character folding that keeps a standard font from wrecking a name.
 *
 * Extracted when the observation agreement needed a second export. The visual tokens are shared
 * because the exports are of the same application and must look it, and `encodable` is shared
 * because it is the subtle one — a second copy of a WinAnsi fold would be a second thing to get
 * right and a second place for it to silently stop being right.
 *
 * Each document keeps its own drawing routines: an assessment is a column of question-and-answer
 * cards and an agreement is running prose, and nothing is gained by pretending those are one shape.
 */

export type Rgb = readonly [number, number, number];

/** The stylesheet's tokens, so the exports and the screen cannot drift apart by accident. */
export const colors = {
  ink: [37, 48, 42],
  body: [74, 90, 98],
  muted: [138, 145, 135],
  green: [78, 106, 31],
  greenDark: [61, 84, 23],
  greenLight: [126, 155, 65],
  mint: [237, 242, 224],
  surface: [255, 255, 255],
  canvas: [244, 245, 240],

  /* The stylesheet's colourless tint, used here for an answer panel with no verdict on it. */
  shade: [242, 243, 239],
  hairline: [226, 230, 218],
  warn: [138, 109, 31],
  warnBackground: [253, 246, 227],
  warnBorder: [224, 207, 154],

  // The assessment's "Incorrect" mark. The stylesheet's muted brick, not a warning colour: amber
  // already means "left blank" on the same card, and the two have to stay apart.
  danger: [168, 58, 52],
  dangerBackground: [251, 238, 238],
  dangerBorder: [229, 188, 186],
} as const satisfies Record<string, Rgb>;

/**
 * Points, A4. The sizes callers use are the stylesheet's pixel values scaled by about 0.7 — 15px of
 * body text becomes 10.5pt, which is a comfortable reading size on paper and keeps every proportion
 * (padding, radii, accent bars) in the same relationship to it as on screen.
 */
export const page = {
  width: 595,
  height: 842,
  margin: 56,
};

export const contentWidth = page.width - page.margin * 2;
export const contentBottom = page.height - page.margin;

export function setText(doc: jsPDF, color: Rgb): void {
  doc.setTextColor(color[0], color[1], color[2]);
}

export function setFill(doc: jsPDF, color: Rgb): void {
  doc.setFillColor(color[0], color[1], color[2]);
}

export function setStroke(doc: jsPDF, color: Rgb): void {
  doc.setDrawColor(color[0], color[1], color[2]);
}

/**
 * Every character these pages can carry: printable Latin-1, the punctuation and the handful of
 * letters PDF puts in the 0x80–0x9F range that Latin-1 leaves as controls, and the three whitespace
 * characters worth keeping.
 *
 * WinAnsi's own range starts at 0, but nothing below a space belongs in text typed into a form, and
 * a stray one is better shown as `?` than handed to the font. Written as escapes rather than as the
 * characters themselves because several of them are invisible.
 */
const winAnsi =
  /^[\t\n\r -ÿŒœŠšŸŽžƒˆ˜–—‘’‚“”„†‡•…‰‹›€™]*$/;

/** Letters whose accent is a stroke through the glyph, which decomposition does not separate. */
const strokes: Readonly<Record<string, string>> = {
  'đ': 'd', // đ
  'Đ': 'D', // Đ
  'ħ': 'h', // ħ
  'Ħ': 'H', // Ħ
  'ł': 'l', // ł
  'Ł': 'L', // Ł
  'ŧ': 't', // ŧ
  'Ŧ': 'T', // Ŧ
};

/**
 * Folds text down to what the standard fonts can actually put on a page.
 *
 * Helvetica here is the viewer's built-in face, which is WinAnsi-encoded and so covers Latin-1 and
 * very little beyond it. `é`, `ñ` and `ö` are fine. Hand it `ć` or `š` and jsPDF falls back to
 * UTF-16, which a standard font cannot do: the letter disappears and every *other* character in the
 * same string comes out spaced apart, so one accent wrecks a whole name rather than one glyph of it.
 *
 * Decomposing first turns most of them into a letter the font does have — `Marković` becomes
 * `Markovic`, which is a name someone recognises as theirs. Whatever survives that becomes `?`,
 * still without taking the rest of the line with it. Rendering them properly would mean embedding a
 * Unicode face, which is the one thing these exports were written to avoid; worth revisiting if
 * these names turn out to be common here rather than occasional.
 */
export function encodable(text: string): string {
  // Almost every string is already fine, and this runs on every line of every document.
  if (winAnsi.test(text)) return text;

  return [...text]
    .map((character) => {
      if (winAnsi.test(character)) return character;

      const folded = character.normalize('NFD').replace(/[̀-ͯ]/g, '');
      if (folded.length > 0 && winAnsi.test(folded)) return folded;

      return strokes[character] ?? '?';
    })
    .join('');
}

/** Wraps `text` to `width` at the given face, without drawing it. */
export function measure(doc: jsPDF, text: string, width: number, size: number, bold: boolean): string[] {
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(size);

  // Folded before wrapping, not before drawing: the widths the wrap is measured against have to be
  // the widths of the characters that end up on the page.
  return doc.splitTextToSize(encodable(text), width) as string[];
}

/** A rounded, filled, outlined badge sized to its own text. Returns the width it occupied. */
export function drawPill(
  doc: jsPDF,
  x: number,
  y: number,
  text: string,
  options: { size: number; paddingX: number; paddingY: number; color: Rgb; background: Rgb; border?: Rgb },
): number {
  const label = encodable(text);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(options.size);

  const width = doc.getTextWidth(label) + options.paddingX * 2;
  const height = options.size + options.paddingY * 2;

  setFill(doc, options.background);

  if (options.border) {
    setStroke(doc, options.border);
    doc.setLineWidth(0.7);
  }

  // A radius of half the height is what makes it a pill rather than a rounded box.
  doc.roundedRect(x, y, width, height, height / 2, height / 2, options.border ? 'FD' : 'F');

  setText(doc, options.color);

  // Optically centred: text occupies baseline-minus-cap-height up to the baseline, so centring that
  // band puts the baseline at y + (height + capHeight) / 2. 0.72 is Helvetica's cap height as a
  // fraction of point size.
  doc.text(label, x + options.paddingX, y + (height + options.size * 0.72) / 2);

  return width;
}

export function pillHeight(size: number, paddingY: number): number {
  return size + paddingY * 2;
}

/**
 * Tracks the pen position down the document and starts new pages.
 *
 * A cursor rather than a measured-up-front layout because the content is a list of variable-height
 * blocks in order, which is the one shape this handles well: each block asks how much room is left,
 * and takes a new page if the answer is "not enough".
 */
export class PdfCursor {
  readonly doc: jsPDF;
  y: number;

  constructor(doc: jsPDF) {
    this.doc = doc;
    this.y = page.margin;
  }

  get available(): number {
    return contentBottom - this.y;
  }

  newPage(): void {
    this.doc.addPage();
    this.y = page.margin;
  }

  /** Moves to a new page if `height` will not fit on this one. */
  reserve(height: number): void {
    if (height > this.available) {
      this.newPage();
    }
  }
}

export function writeParagraph(
  cursor: PdfCursor,
  text: string,
  options: { size: number; lineHeight: number; bold?: boolean; color: Rgb; indent?: number },
): void {
  const { doc } = cursor;
  const indent = options.indent ?? 0;
  const lines = measure(doc, text, contentWidth - indent, options.size, options.bold ?? false);

  setText(doc, options.color);

  for (const line of lines) {
    cursor.reserve(options.lineHeight);
    doc.text(line, page.margin + indent, cursor.y + options.size);
    cursor.y += options.lineHeight;
  }
}

export const sectionBarStyle = { size: 10.5, paddingX: 10, paddingY: 5.5, radius: 4, gapAfter: 10 } as const;

/** The filled green bar both exports use for a section heading. */
export function drawSectionBar(cursor: PdfCursor, title: string): void {
  const { doc } = cursor;
  const lines = measure(doc, title, contentWidth - sectionBarStyle.paddingX * 2, sectionBarStyle.size, true);
  const height = lines.length * (sectionBarStyle.size + 4) + sectionBarStyle.paddingY * 2;

  // A heading alone at the foot of a page is the one break worth preventing by hand, so it is
  // reserved together with enough room for what follows it to start underneath.
  cursor.reserve(height + 60);

  setFill(doc, colors.green);
  doc.roundedRect(page.margin, cursor.y, contentWidth, height, sectionBarStyle.radius, sectionBarStyle.radius, 'F');

  setText(doc, colors.surface);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(sectionBarStyle.size);

  lines.forEach((line, index) => {
    doc.text(
      line,
      page.margin + sectionBarStyle.paddingX,
      cursor.y + sectionBarStyle.paddingY + sectionBarStyle.size + index * (sectionBarStyle.size + 4),
    );
  });

  cursor.y += height + sectionBarStyle.gapAfter;
}

/** Draws a hairline rule the full width of the content column. */
export function drawRule(cursor: PdfCursor): void {
  setStroke(cursor.doc, colors.hairline);
  cursor.doc.setLineWidth(0.5);
  cursor.doc.line(page.margin, cursor.y, page.width - page.margin, cursor.y);
}

/**
 * A person's name as a filename fragment someone can find again in a downloads folder. Accents are
 * stripped so it survives being emailed around; ASCII only from here.
 */
export function filenameSlug(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

/** The name and page number along the foot of every page. Called once the page count is final. */
export function drawFooters(doc: jsPDF, label: string): void {
  const pages = doc.getNumberOfPages();

  for (let index = 1; index <= pages; index++) {
    doc.setPage(index);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setText(doc, colors.muted);
    doc.text(encodable(label), page.margin, page.height - page.margin + 20);
    doc.text(`Page ${index} of ${pages}`, page.width - page.margin, page.height - page.margin + 20, {
      align: 'right',
    });
  }
}
